import { useEffect, useState } from 'react'
import { getLocations } from '../../api/master'
import { getAreas, getSkuBins } from '../../api/inventory'
import { num } from '../sales/salesShared'

// Helpers shared by the Inventory pages.

/** At or below this many on hand counts as running low (same as the Overview). */
export const LOW_STOCK = 5

/** In stock / Running low / Out of stock, for badges and filters. */
export function stockState(row) {
  const qty = num(row?.qty_on_hand)
  if (qty <= 0) return { id: 'out', label: 'Out of stock', tone: 'bad' }
  if (qty <= LOW_STOCK) return { id: 'low', label: 'Running low', tone: 'warn' }
  return { id: 'ok', label: 'In stock', tone: 'good' }
}

/** What one unit cost on average: the product's average cost, else its cost. */
export const unitCost = (row) => num(row?.avg_cost ?? row?.unit_cost)

/** On hand at average cost. */
export const stockValue = (row) => Math.max(0, num(row?.qty_on_hand)) * unitCost(row)

/** 1,250 or 2.5, without trailing zeros. */
export const qty = (v) => num(v).toLocaleString(undefined, { maximumFractionDigits: 3 })

/** "Mama Gold Rice 50kg · RICE-50" */
export const itemLabel = (p) => [p?.name, p?.sku].filter(Boolean).join(' · ')

/** Stock items only: services have no stock. */
export const stockItems = (rows) => (Array.isArray(rows) ? rows : []).filter((r) => !Number(r?.is_service))

/** The tenant's locations, loaded once per page. */
export function useLocations() {
  const [locations, setLocations] = useState(null)
  useEffect(() => {
    getLocations().then((ls) => setLocations(Array.isArray(ls) ? ls : [])).catch(() => setLocations([]))
  }, [])
  return locations
}

/** Stock movement types in plain words. */
export const MOVE_TYPES = {
  transfer: 'Moved',
  adjust_in: 'Added',
  adjust_out: 'Removed',
  purchase: 'Received',
  sale: 'Sold',
  consumption: 'Used',
  refund_in: 'Returned by customer',
  refund_out: 'Returned to vendor',
  putaway: 'Put away',
}

/** "Dry goods - /A1/S2/B3": a bin's department and path. */
export const binLabel = (a) => {
  const path = a?.path || a?.code || a?.name || ''
  const dept = String(a?.department_name || '').trim()
  return dept && !path.startsWith(`/${dept}`) ? `${dept} - ${path}` : path
}

/** The active bins at one location (or every area when none is marked Bin). */
export function useBins(locationId) {
  const [bins, setBins] = useState([])
  useEffect(() => {
    if (!locationId) { setBins([]); return undefined }
    let cancelled = false
    getAreas(locationId).then((rows) => {
      if (cancelled) return
      const active = (rows || []).filter((a) => Number(a.is_active ?? 1) !== 0)
      const onlyBins = active.filter((a) => String(a.level || '').toLowerCase() === 'bin')
      setBins(onlyBins.length ? onlyBins : active)
    }).catch(() => { if (!cancelled) setBins([]) })
    return () => { cancelled = true }
  }, [locationId])
  return bins
}

/** Which bins hold an item: { rows: [{location_id, location, area_id, path, qty}], pick_path, picks }. */
export function useItemBins(sku, version = 0) {
  const [data, setData] = useState(null)
  useEffect(() => {
    if (!sku) { setData(null); return undefined }
    let cancelled = false
    getSkuBins(sku).then((d) => { if (!cancelled) setData(d || { rows: [] }) }).catch(() => { if (!cancelled) setData({ rows: [] }) })
    return () => { cancelled = true }
  }, [sku, version])
  return data
}

/**
 * What a printed label's barcode holds: the area's own barcode, else its
 * path. With more than one location the same path can exist at each, so
 * the location goes in front ("L4:/DRY/A1/S1/SH1/B1") and a scan can never
 * open the same-named bin at another location.
 */
export const labelCode = (a, multiLocation = false) => String(a?.barcode || '').trim() || (multiLocation ? `L${a?.location_id}:${a?.path || ''}` : (a?.path || ''))

/**
 * The storage area a scanned label (or typed code) points to: its barcode
 * first, then its path. With several locations sharing a typed path, the
 * one at `locationId` wins, else the only one `prefer(area)` accepts (e.g.
 * the bin that holds the item). Returns { area } or { error }.
 */
export function findArea(areas, raw, locationId, prefer) {
  const code = String(raw || '').trim()
  if (!code) return { error: 'Scan or type the label first.' }
  const lower = code.toLowerCase()
  const byBarcode = (areas || []).filter((a) => String(a.barcode || '').trim().toLowerCase() === lower)
  if (byBarcode.length === 1) return { area: byBarcode[0] }
  // A printed label from a business with several locations: L<location>:<path>.
  const tagged = code.match(/^L(\d+):(.+)$/i)
  if (tagged) {
    const at = (areas || []).find((a) => String(a.location_id) === tagged[1] && String(a.path || '').toLowerCase() === tagged[2].toLowerCase())
    return at ? { area: at } : { error: `Nothing has the label ${tagged[2]}. It may have been renamed; print a new label.` }
  }
  const path = (code.includes('/') ? `/${code.replace(/^\/+|\/+$/g, '')}` : code).toLowerCase()
  let byPath = (areas || []).filter((a) => String(a.path || '').toLowerCase() === path)
  const here = byPath.filter((a) => String(a.location_id) === String(locationId))
  if (here.length) byPath = here
  if (byPath.length > 1 && prefer) {
    const preferred = byPath.filter(prefer)
    if (preferred.length === 1) byPath = preferred
  }
  if (byPath.length === 1) return { area: byPath[0] }
  if (byPath.length > 1) return { error: `More than one location has ${code}. Choose the location first.` }
  return { error: `Nothing has the label ${code}.` }
}

/** Every storage area at every location, for reading scanned labels. */
export function useAllAreas() {
  const [areas, setAreas] = useState([])
  useEffect(() => {
    let cancelled = false
    getAreas().then((rows) => { if (!cancelled) setAreas(Array.isArray(rows) ? rows : []) }).catch(() => {})
    return () => { cancelled = true }
  }, [])
  return areas
}

/** A stock count's status in plain words, with its badge tone. */
export const COUNT_STATUS = {
  counting: { label: 'Counting', tone: 'info' },
  waiting: { label: 'Waiting for approval', tone: 'warn' },
  posted: { label: 'Stock updated', tone: 'good' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
}

/** "Count 7" */
export const countTitle = (c) => (c?.count_no ? `Count ${c.count_no}` : 'Count')

/** An id for one scan, so sending it twice never counts it twice. */
export const newScanId = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`)
